import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Button, Divider, Text
} from 'components/ui';
import PazePaymentMethod from 'components/Paze/PazePaymentMethod';
import {
    colors, fontSizes, fontWeights, mediaQueries, space, lineHeights
} from 'style/config';

const PazeExpandedView = ({
    pazeHeader, pazeSubtitle, pazeGiftCardWarning, pazeSaveAndContinue, pazeCancel, onSaveAndContinue, onCancel
}) => {
    const headerRef = useRef(null);

    useEffect(() => {
        headerRef.current?.focus();
    }, []);

    return (
        <Box
            role='region'
            aria-labelledby='paze-expanded-header'
            data-testid='paze-expanded-view'
            data-at={Sephora.debug.dataAt('paze_expanded_view')}
        >
            <Text
                as='h3'
                id='paze-expanded-header'
                ref={headerRef}
                tabIndex={-1}
                css={styles.header}
            >
                {pazeHeader}
            </Text>
            <Text
                css={styles.subtitle}
                as='p'
            >
                {pazeSubtitle}
            </Text>
            <Text
                css={styles.giftCardWarning}
                as='p'
                marginBottom={[3, 0]}
            >
                {pazeGiftCardWarning}
            </Text>
            <Box css={styles.imageWrapper}>
                <PazePaymentMethod
                    isFrictionless
                    frameStyles={styles.iframe}
                    legacyTextStyles={styles.legalNotice}
                    shouldLoadIframe={false}
                />
            </Box>
            <Divider css={styles.divider} />
            <Box css={styles.buttonContainer}>
                <Button
                    variant='secondary'
                    css={styles.cancelButton}
                    onClick={onCancel}
                    data-testid='paze-cancel'
                    data-at={Sephora.debug.dataAt('paze_cancel_button')}
                >
                    {pazeCancel}
                </Button>
                <Button
                    variant='primary'
                    css={styles.saveButton}
                    onClick={onSaveAndContinue}
                    data-testid='paze-save-continue'
                    data-at={Sephora.debug.dataAt('paze_save_continue_button')}
                >
                    {pazeSaveAndContinue}
                </Button>
            </Box>
        </Box>
    );
};

const styles = {
    header: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.base,
        marginTop: space[4],
        [mediaQueries.sm]: {
            marginTop: space[3]
        }
    },
    subtitle: {
        color: colors.black,
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.base,
        [mediaQueries.xsMax]: {
            color: colors.gray
        }
    },
    giftCardWarning: {
        color: colors.black,
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.base,
        marginTop: space[2]
    },
    imageWrapper: {
        marginTop: space[2],
        marginBottom: space[1],
        width: '100%',
        overflow: 'hidden',
        [mediaQueries.sm]: {
            marginBottom: space[3]
        }
    },
    iframe: {
        height: '277px',
        marginTop: '-34px',
        marginBottom: '-34px',
        width: 'auto'
    },
    legalNotice: {
        fontSize: `${fontSizes.sm}px`,
        marginTop: space[2],
        lineHeight: lineHeights.tight,
        [mediaQueries.sm]: {
            marginTop: 0
        }
    },
    divider: {
        display: 'none',
        [mediaQueries.sm]: {
            display: 'block',
            marginTop: space[4],
            marginBottom: space[4],
            borderColor: colors.lightGray
        }
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: space[3],
        [mediaQueries.sm]: {
            flexDirection: 'row',
            justifyContent: 'flex-start'
        }
    },
    cancelButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    },
    saveButton: {
        width: '100%',
        [mediaQueries.sm]: {
            width: 'auto',
            minWidth: '310px'
        }
    }
};

PazeExpandedView.propTypes = {
    pazeHeader: PropTypes.string,
    pazeSubtitle: PropTypes.string,
    pazeGiftCardWarning: PropTypes.string,
    pazeLegalNotice: PropTypes.string,
    pazeSaveAndContinue: PropTypes.string,
    pazeCancel: PropTypes.string,
    onSaveAndContinue: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(PazeExpandedView, 'PazeExpandedView');
